import { useState, useRef, useEffect, useCallback } from 'react';
import { Field } from '@/shadcn/components/ui/field';
import { Label } from '@/shadcn/components/ui/label';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { XIcon } from 'lucide-react';
import { ChevronDown, ChevronUp as ChevronUpIcon } from 'lucide-react';
import { MOTION } from './motion';

export interface MultiSelectPopoverItem {
    id: string;
    value: string;
}

/* Vybrana pilulka v triggeri. */
// vyska 30px = presne chip v pulldowne (CHIP_BASE: 12px text + py-7); h-full sa
// natahovalo na vysku pola a pilulka bola vyssia ako tie v menu
const TRIGGER_PILL_BASE =
    'h-[30px] shrink-0 flex items-center gap-[3px] bg-[#f8ebb4] px-[12px] pr-[9px] rounded-full whitespace-nowrap';
const TRIGGER_PILL = cn(TRIGGER_PILL_BASE, 'shrink-0');
const TRIGGER_GAP = 5; // zhodne s gap-[5px]
/* Odhad sirky "+N" pilulky (vratane gapu) pri rozhodovani, kolko sa zmesti. */
const OVERFLOW_CHIP_W = 46;

/**
 * Vybrane pilulky v triggeri BEZ horizontalneho scrollu. Zmesti len tolko,
 * kolko sa vojde (s rezervou vpravo na otvorenie), zvysok schova do "+N".
 * Sirky merame v skrytej vrstve, aby +N sedelo presne.
 */
function TriggerItems(props: {
    items: MultiSelectPopoverItem[];
    showDelete: boolean;
    onDelete?: (item: MultiSelectPopoverItem) => void;
}) {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(props.items.length);

    useEffect(() => {
        const row = rowRef.current;
        const meas = measureRef.current;
        if (!row || !meas) return;

        const compute = () => {
            // chevron uz zabera vlastne miesto vpravo (flex sibling),
            // takze tu sa pocita s celou sirkou riadku
            const avail = row.clientWidth;
            const widths = Array.from(meas.children).map((c) => (c as HTMLElement).offsetWidth);
            let used = 0;
            let count = 0;
            for (let i = 0; i < widths.length; i++) {
                const w = widths[i] + (i > 0 ? TRIGGER_GAP : 0);
                if (used + w > avail) break;
                used += w;
                count++;
            }
            // ak sa nezmestili vsetky, rezervuj miesto pre "+N" pilulku
            if (count < widths.length) {
                while (count > 0 && used + OVERFLOW_CHIP_W > avail) {
                    used -= widths[count - 1] + (count - 1 > 0 ? TRIGGER_GAP : 0);
                    count--;
                }
            }
            // vzdy ukaz aspon jednu pilulku (skratenu), nikdy len samotne "+N" -
            // pri jednej dlhej hodnote (napr. cenovy rozsah) by "+1" nic nepovedalo
            if (widths.length > 0 && count === 0) count = 1;
            setVisible(count);
        };

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(row);
        return () => ro.disconnect();
    }, [props.items]);

    const overflow = props.items.length - visible;

    return (
        <div
            ref={rowRef}
            className="relative w-full h-full flex items-center gap-[5px] overflow-hidden"
        >
            {/* skryta meracia vrstva - vsetky pilulky pre presne sirky */}
            <div
                ref={measureRef}
                aria-hidden
                className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-[5px]"
            >
                {props.items.map((item, idx) => (
                    <div key={idx} className={TRIGGER_PILL}>
                        <small className="text-[12px] font-[500]">{item.value}</small>
                        {props.showDelete && <XIcon className="w-[13px]" />}
                    </div>
                ))}
            </div>

            {props.items.slice(0, visible).map((item, idx) => (
                <div
                    key={idx}
                    // jedina viditelna pilulka sa smie zmensit a text skratit,
                    // aby sa dlha hodnota zmestila namiesto kolapsu do "+N"
                    className={cn(TRIGGER_PILL_BASE, visible === 1 ? 'min-w-0' : 'shrink-0')}
                >
                    <small className={cn('text-[12px] font-[500]', visible === 1 && 'truncate')}>
                        {item.value}
                    </small>
                    {props.showDelete && (
                        <XIcon
                            className="w-[13px] shrink-0 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onDelete?.(item);
                            }}
                        />
                    )}
                </div>
            ))}

            {overflow > 0 && (
                <div
                    className={cn(
                        // sirka +N rastie s volnym miestom, ale s rozumnym maximom
                        // (nech to nie je siroky prazdny pruh cez cely field).
                        // min/max cez inline style - Tailwind arbitrary max-w tu obcas
                        // nevygeneruje CSS v dev JIT.
                        'h-[30px] flex items-center justify-center bg-[#f8ebb4] px-[9px]',
                        'rounded-full whitespace-nowrap flex-1',
                    )}
                    style={{ minWidth: 40, maxWidth: 64 }}
                    title={`+${overflow}`}
                >
                    <small className="text-[12px] font-[600] text-black/70">+{overflow}</small>
                </div>
            )}
        </div>
    );
}

interface Props {
    className?: string | string[];
    open?: boolean;
    type: 'classic' | 'multi';
    children: React.ReactElement;
    items: MultiSelectPopoverItem[];
    label?: string;
    placeholder?: string;
    iconLeft?: React.ReactElement;
    /** Bez bieleho ringu a bielej vyplne - pre fieldy na glass/tmavych plochach.
        Dropdown panel ostava biely kvoli citatelnosti. */
    bare?: boolean;
    /** Extra triedy na rozbaleny panel - napr. min-width a zarovnanie
        (md:min-w-[260px], md:left-auto md:right-0), aby uzke stlpce nestlacali obsah. */
    menuClassName?: string | string[];
    /** classic normalne po zmene items zavrie panel (vyber = hotovo). Pri
        priebeznych ovladacoch (slider) to nechceme - panel ostava otvoreny. */
    keepOpenOnItemsChange?: boolean;
    onChangeQuery?: (e: string) => void;
    onDeleteItem?: (item: MultiSelectPopoverItem) => void;
}

export function MultiSelectPopover(props: Props) {
    const [items, setItems] = useState<Array<MultiSelectPopoverItem>>([]);
    const [selected, setSelected] = useState([]);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    /** Podpis poslednej selekcie - aby classic zavrel panel LEN pri realnej
        zmene vyberu, nie pri kazdom re-rendri rodica (napr. rotujuci
        placeholder v hero), ktory dava novu referenciu items. */
    const lastSelectionRef = useRef<string | null>(null);
    /** Searchable = multi pole s textovym vyhladavanim (lokality). Bez
        onChangeQuery (napr. typ nehnutelnosti) je to len viacnasobny vyber -
        vtedy chceme v triggeri ukazovat vybrane pilulky aj ked je panel otvoreny,
        nie prazdny textovy input. */
    const searchable = props.type === 'multi' && !!props.onChangeQuery;

    const scrollToRight = () => {
        const el = scrollRef.current;
        if (!el) return;

        el.scrollTo({
            left: el.scrollWidth,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        const handlePointerDownOutside = (event: PointerEvent) => {
            const target = event.target as Node | null;

            if (!containerRef.current || !target) return;

            if (!containerRef.current.contains(target)) {
                setOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDownOutside);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDownOutside);
        };
    }, []);

    useEffect(() => {
        if (typeof props.open !== 'undefined') {
            setOpen(props.open);
        }
    }, [props.open]);

    useEffect(() => {
        if (!open || props.type !== 'multi') return;

        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, [open, props.type]);

    useEffect(() => {
        scrollToRight();
    }, [props.items.length, query]);

    useEffect(() => {
        setItems(props.items);

        // classic: po vybere zavri panel - ale iba ked sa vyber naozaj zmenil.
        // Rodic tvori novu items referenciu pri kazdom re-rendri (rotujuci
        // placeholder, formik...), preto porovnavame obsah, nie referenciu.
        const signature = props.items.map((i) => i.id).join('|');
        if (
            props.type === 'classic' &&
            !props.keepOpenOnItemsChange &&
            props.items.length > 0 &&
            signature !== lastSelectionRef.current
        ) {
            setOpen(false);
        }
        lastSelectionRef.current = signature;
    }, [props.items]);

    useEffect(() => {
        props.onChangeQuery?.(query);
    }, [query]);

    return (
        <Field className={cn('min-w-0 md:flex cursor-pointer', props.className)}>
            {props.label && !props.bare && (
                <Label
                    htmlFor="location"
                    className="text-[13px] -mb-[5px] font-[500] pl-[13px] xl:text-[14px]"
                >
                    {props.label}
                </Label>
            )}

            <div
                ref={containerRef}
                className={cn(
                    'relative z-[1000] rounded-[30px]',
                    props.bare ? 'p-0 bg-transparent' : 'p-[5px] bg-[#fff]',
                )}
            >
                <div
                    className={cn(
                        'w-full text-[13px] py-[6px] border flex items-center px-[6px] relative z-[1000] overflow-hidden',
                        props.bare ? 'h-[40px] md:h-[44px] xl:h-[50px]' : 'h-[40px] xl:h-[45px]',
                        'rounded-[30px]',
                        props.bare
                            ? cn(
                                  'border-transparent text-black/80',
                                  // s vybranou hodnotou field ozije do plnej bielej
                                  props.items.length > 0
                                      ? 'bg-white text-black shadow-[0_11px_27px_0_rgba(0,0,0,0.10)]'
                                      : 'bg-[rgba(255,255,255,.5)]',
                                  'duration-[260ms] hover:bg-white hover:text-black',
                                  'hover:shadow-[0_11px_27px_0_rgba(0,0,0,0.14)]',
                                  // roztvoreny field lezi na bielom paneli - bez tiena
                                  open && 'bg-white text-black shadow-none hover:shadow-none',
                                  MOTION,
                              )
                            : 'bg-white',
                    )}
                    onClick={() => setOpen((o) => !o)}
                >
                    {props.iconLeft && (
                        <div
                            className={cn(
                                'px-[5px] xl:px-[7px] shrink-0',
                                props.bare ? 'text-black/80' : 'text-[#c8c8c8]',
                            )}
                        >
                            {props.iconLeft}
                        </div>
                    )}
                    <div className={cn('w-full h-full flex items-center overflow-hidden')}>
                        {(!open || !searchable) && props.items?.length > 0 ? (
                            <TriggerItems
                                items={props.items}
                                showDelete={props.type === 'multi'}
                                onDelete={props.onDeleteItem}
                            />
                        ) : (
                            <></>
                        )}

                        {open && searchable && (
                            <div className="flex-1 h-full flex items-center px-[5px]">
                                <input
                                    id="multi-select-popover-input"
                                    ref={inputRef}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    // klik do inputu nesmie prebublat na toggle (zavrel by panel)
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={() => {
                                        setOpen(true);
                                        scrollToRight();
                                    }}
                                    className={cn(
                                        'w-full h-full px-[5px]',
                                        // !items.length ? '' : 'min-w-[90px] w-[90px]',
                                        'bg-transparent outline-none border-0 shadow-none',
                                        'text-[13px] whitespace-nowrap',
                                        props.bare
                                            ? 'placeholder:text-black/85 placeholder:font-[500] placeholder:whitespace-nowrap'
                                            : 'placeholder:text-muted-foreground placeholder:whitespace-nowrap',
                                    )}
                                    placeholder={props.placeholder ?? ''}
                                />
                            </div>
                        )}

                        {!props.items.length &&
                            !(open && searchable) &&
                            props.placeholder && (
                                <div className="px-[10px]">
                                    <p
                                        className={cn(
                                            props.bare ? 'text-black/85 font-[500]' : 'text-muted-foreground',
                                        )}
                                    >
                                        {props.placeholder}
                                    </p>
                                </div>
                            )}

                    </div>

                    {/* Chevron vpravo - vizualny signal, ze pole je rozbalovacie.
                        Ma ZHODNY padding ako lavy icon wrapper, takze medzera od
                        praveho okraja je presne zrkadlom lavej ikony. Zaroven drzi
                        volne miesto na otvorenie, aj ked je pole plne piluliek. */}
                    <div
                        aria-hidden
                        className={cn(
                            'px-[5px] xl:px-[7px] shrink-0 flex items-center',
                            props.bare ? 'text-black/45' : 'text-black/35',
                        )}
                    >
                        <ChevronDown
                            className={cn(
                                'size-[15px]',
                                'transition duration-[260ms] ease-[var(--ease-rx)]',
                                open && 'rotate-180',
                            )}
                        />
                    </div>
                </div>
                <div
                    className={cn(
                        // panel sa otvara POD trigger (top-full), nie cez neho - inak
                        // by prekryval susedne polia v riadku. Padne na obsah pod filtrom.
                        // overflow-hidden: scrollbar sa orezava do zaobleneho tvaru
                        // panela (inak vychadza von cez zaobleny roh).
                        'absolute left-0 top-full mt-[8px] bg-white w-full z-[999] overflow-hidden',
                        props.bare
                            ? 'rounded-[24px] shadow-[0_24px_70px_rgba(0,0,0,0.18)] ring-1 ring-black/[.05]'
                            : 'rounded-[25px] shadow-[0_12px_40px_rgba(0,0,0,0.14)]',
                        // plynule otvaranie - panel je stale mounted, prepina sa opacity + scale
                        'origin-top transition duration-[260ms] ease-[var(--ease-rx)]',
                        open
                            ? 'opacity-100 scale-100 pointer-events-auto'
                            : 'opacity-0 scale-[.97] pointer-events-none',
                        props.menuClassName,
                    )}
                >
                    <div className={cn(props.bare ? 'pt-[6px]' : 'pt-[4px]')}>
                        <div
                            className={cn(
                                'max-h-[400px] overflow-auto',
                                // rezervovany priestor pre scrollbar - kontent pri jeho
                                // nabehnuti neuskoci; tenky styl cez vox-scroll-light
                                '[scrollbar-gutter:stable] [scrollbar-width:thin] vox-scroll-light',
                                props.bare ? 'px-[8px] pb-[10px] pt-[4px]' : 'p-[10px]',
                            )}
                        >
                            {props.children}
                        </div>
                        {/* jemna sipka na zatvorenie - druhy sposob popri kliku vedla a Esc */}
                        <button
                            type="button"
                            aria-label="Zavrieť"
                            tabIndex={open ? 0 : -1}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                            }}
                            className={cn(
                                'group/close w-full flex items-center justify-center',
                                'h-[26px] text-black/25 hover:text-black/60',
                                'transition duration-200 ease-[var(--ease-rx)]',
                            )}
                        >
                            <ChevronUpIcon
                                className={cn(
                                    'w-[15px] transition duration-200 ease-[var(--ease-rx)]',
                                    'group-hover/close:-translate-y-[1px]',
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </Field>
    );
}
